// transferIn 純函式抽取。
// 目的：將 transferIn/ 各元件內與 UI 無關的判斷／計算邏輯抽成純函式，供單元測試鎖住既有行為（regression）。
// 命名與風格比照 create/membershipFormRules.js。
import { isEqual, isNumber } from '@util/utils';
import { isValidDate, parseDate, isAfter, getAddMonths, getAddDays, getFirstOfMonth } from '@util/moment';
import { accAdd } from '@util/math';
import { MEMBERSHIP_ITEM_TYPE, PEOPLE_TYPE, TRANSFERIN_ENTEREDTYPE } from '@icoach/constants';
import { default as noticeData } from '@icoach/documents/membership/create/notice';
import membershipNoticeData105 from '@icoach/documents/membership/create/versions/v105/notice';
import membershipNoticeData106 from '@icoach/documents/membership/create/versions/v106/notice';
import { resolvePrivacyItemIndex } from '@icoach/documents/membership/create/versions';

// ── A 會員須知條款判斷（來源：MembershipTransferInSurvey.jsx L12-23）──
// 依「原會籍是否續約」與「會員類型」決定須知條款分類。
export const checkMembershipType = (originalIsRenew, memberType) => {
    let target;
    if (isEqual(memberType, 6)) {
        target = 'newStudent';
    } else if (originalIsRenew) {
        target = 'renewal';
    } else {
        target = 'new';
    }
    return target;
};

// ── B 贈品項目合併（來源：MembershipTransferInDiscountDateItem.jsx L10-28）──
// 將多筆贈品項目依「前贈/後贈」兩種類型合併加總月數、天數。
// 注意：輸出順序由 Object.values() 決定，數字型 key（21、22）一律以數字升冪排列，
// 與輸入陣列的原始順序無關（此為 JavaScript 物件 key 列舉規則，非邏輯錯誤）。
export const mergeItemsTypeData = (targetData = []) => {
    const mergerData = targetData.reduce((acc, { itemType, applyMonth, applyDay }) => {
        let newAcc = { ...acc };
        const num = isEqual(itemType, MEMBERSHIP_ITEM_TYPE.INFRONTGIFT) ? MEMBERSHIP_ITEM_TYPE.INFRONTGIFT : MEMBERSHIP_ITEM_TYPE.FOLLOWINGGIFT;
        if (newAcc[num]) {
            newAcc[num].applyMonth = accAdd(newAcc[num].applyMonth, applyMonth);
            newAcc[num].applyDay = accAdd(newAcc[num].applyDay, applyDay);
        } else {
            newAcc[num] = { itemType: num, itemChecked: true, applyMonth, applyDay };
        }
        return newAcc;
    }, {});
    return Object.values(mergerData);
};

// ── C 付款方式錯誤訊息映射（來源：MembershipTransferInPaymentModeItem.jsx L7-25, 93-102）──
export const keyMappings = {
    creditCard: {
        paymentCreditCardBankName: 'creditCardBankName',
        paymentCreditCardType: 'creditCardType',
        paymentCreditCardNo: 'creditCardNo',
        paymentCardExpiryDate: 'cardExpiryDate',
        paymentCardName: 'cardName',
        paymentCardSign: 'signImg',
        paymentCardship: 'cardRelation',
    },
    bank: {
        paymentBankCode: 'bankCode',
        paymentBankName: 'bankName',
        paymentBankAccount: 'bankAccount',
        paymentBankAccountName: 'bankAccountName',
        paymentBankAccountID: 'bankAccountID',
        bankCardSignImg: 'signImg',
    },
};

export const mapErrorMessages = (errorModule, keyMapping) => {
    return Object.keys(keyMapping).reduce((result, keyName) => {
        const componentKey = keyMapping[keyName];
        const errorMsg = errorModule[keyName];
        if (errorMsg) {
            result[componentKey] = errorMsg;
        }
        return result;
    }, {});
};

// ── D 付款/扣款資訊（來源：MembershipTransferInPaymentAuthorization.jsx L65, 107-108）──
export const getPaymentTypeText = (paymentTypeOptions = [], v) =>
    paymentTypeOptions.find(({ value }) => String(value) === String(v))?.text ?? '';

export const shouldShowDebitSection = (debitFirstDate, memberShipEndDate) =>
    !(memberShipEndDate && isAfter(debitFirstDate, memberShipEndDate, 'YYYY-MM-DD'));

export const resolveDefaultDebitEndDate = memberShipEndDate =>
    memberShipEndDate ? parseDate(getFirstOfMonth(memberShipEndDate)) : '';

// ── E 停權/贈品項目結束日計算（來源：MembershipTransferInSuspendDateItem.jsx L37-51, 53-66, 21）──
export const calculateEndDate = (startDate, applyMonth, applyDay) => {
    if (!isValidDate(startDate)) return '';
    const start = parseDate(startDate);
    const month = isNumber(applyMonth) ? Number(applyMonth) : 0;
    const day = isNumber(applyDay) ? Number(applyDay) : 0;
    const end = getAddDays(getAddMonths(start, month), day - 1);
    return end >= start ? end : '';
};

export const shouldHideApplyDay = itemType => itemType === '11';

// ── F 起繳金額初始值（來源：MembershipTransferInFee.jsx L23-24）──
// 現金與信用卡起繳金額共用同一規則：未立即開立時一律為 0。
export const resolveInitialPaymentAmount = (isImmediatelyIssuing, amount) => (isImmediatelyIssuing ? amount || 0 : 0);

// ── G 會籍區間試算參數組裝（來源：MembershipTransferInOrigingMembership.jsx L100-111）──
// items 順序：originalSuspendItems 在前、originalGiftItems 在後。
export const buildOriginalMembershipRangeParams = (data = {}) => {
    const {
        originalMemberType,
        originalMembershipTerm,
        originalContractStartDate,
        originalContractEndDate,
        originalNextMembershipStartDate,
        originalNextMembershipEndDate,
        originalSuspendItems,
        originalGiftItems,
    } = data;
    return {
        memberType: originalMemberType,
        membershipTerm: originalMembershipTerm,
        startDate: originalContractStartDate,
        endDate: originalContractEndDate,
        nextStartDate: originalNextMembershipStartDate,
        nextEndDate: originalNextMembershipEndDate,
        items: [...(originalSuspendItems || []), ...(originalGiftItems || [])],
    };
};

// 來源：MembershipTransferInModifyMembership.jsx L106-118
// items 順序：giftItems 在前、suspendItems 在後（與上方 buildOriginalMembershipRangeParams 相反，為既有行為差異，暫不統一）。
export const buildModifyMembershipRangeParams = (data = {}) => {
    const {
        originalMemberType,
        originalMembershipTerm,
        contractStartDate,
        contractEndDate,
        nextMembershipStartDate,
        nextMembershipEndDate,
        giftItems,
        suspendItems,
    } = data;
    return {
        memberType: originalMemberType,
        membershipTerm: originalMembershipTerm,
        startDate: contractStartDate,
        endDate: contractEndDate,
        nextStartDate: nextMembershipStartDate,
        nextEndDate: nextMembershipEndDate,
        items: [...(giftItems || []), ...(suspendItems || [])],
    };
};

// ── H 頁面身分/流程判斷（來源：MembershipTransferInPage.jsx L47, 66, 84）──
export const resolveDefaultTargetType = enteredType =>
    isEqual(enteredType, TRANSFERIN_ENTEREDTYPE.ENTERED) ? PEOPLE_TYPE.ALREADYTRANSFERINMEMBER : PEOPLE_TYPE.TRANSFERINMEMBER;

export const resolveEnteredTypeByTargetType = targetType =>
    isEqual(targetType, PEOPLE_TYPE.ALREADYTRANSFERINMEMBER) ? TRANSFERIN_ENTEREDTYPE.ENTERED : TRANSFERIN_ENTEREDTYPE.UNENTERED;

export const isManualWithoutReference = (peopleType, referenceID) =>
    isEqual(peopleType, PEOPLE_TYPE.TRANSFERMANUALLY) && (referenceID === 0 || referenceID === '');

// ── I 會員須知清單 ────────────────────────────────────
// 來源：MembershipTransferInSurvey.jsx L22-71 的 switch，原封搬出。
// 搬出來的理由：個資卡片要放在 items 陣列的第幾格 = 須知有幾張卡，
// 而移入的須知條數隨原會籍版本浮動（103/104 還再分新約／續約／學生）。
// 這段原本埋在 Survey 元件裡，Content 拿不到就算不出索引，兩邊共用才保證一致。
//
// 注意：這裡刻意不改用入會的 resolveVersion()。兩邊決定 new/renewal/newStudent
// 的判斷順序相反（入會先問續約、移入先問學生），直接換會改變「又續約又是學生」
// 這種會員看到的條款內容。統一那兩支要另外開票處理。
//
// 回傳 null 代表這一版的須知還無法確定（原 switch 的 default），畫面只顯示提示訊息。
export const resolveTransferInNoticeList = ({ originalContractVersion, originalIsRenew, memberType } = {}) => {
    switch (String(originalContractVersion)) {
        case '105':
            return membershipNoticeData105;
        case '106':
            return membershipNoticeData106;
        case '101':
        case '102':
            return noticeData[originalContractVersion][originalIsRenew ? 'renewal' : 'new'];
        case '103':
        case '104':
            return noticeData[originalContractVersion][checkMembershipType(originalIsRenew, memberType)];
        default:
            return null;
    }
};

// hook 判斷「會員須知這一步填完了沒」需要的三樣東西，一次算好回傳。
// data 就是 temporarySourceDataRef.current（移入的資料放在一顆 ref 裡）。
//
// 為什麼要包成一個函式：hook 是入會與移入共用的，它不知道兩邊資料放在哪、
// 個資索引怎麼算。由各自的呼叫端算好交給它，hook 只負責判斷。
export const resolveTransferInSurveyState = (data = {}) => {
    const noticeList = resolveTransferInNoticeList(data);

    return {
        contractSurvey: data.contractSurvey,
        // 個資告知只有原會籍 106 的會員才需要重新簽署，其餘版本不顯示卡片（回 null，只檢查總同意與簽名）。
        // 106 的須知固定七條，索引與入會一致，故直接沿用 versions/ 的對照表，不再自行計算清單長度。
        privacyItemIndex: resolvePrivacyItemIndex(data.originalContractVersion),
        // 清單算不出來（教練還沒填原會籍）→ 一律視為未完成，不放行。
        isNoticeReady: noticeList !== null,
    };
};